import * as React from "react";
import { UitkCard, UitkCardContent, UitkCardTextSegment } from "uitk-react-cards";
import { viewContext } from "../CheckPrices";
import { UitkLayoutFlex } from "uitk-react-layout-flex";
import { UitkHeading } from "uitk-react-text";
import { UitkSpacing } from "uitk-react-spacing";
import { useLocalization } from "@shared-ui/localization-context";
import IsomorphicCarousel from "src/components/shared/IsomorphicCarousel/IsomorphicCarousel";
import { HotwireContainer } from "./views/HotwireContainer";

export interface ContainerProps {
  id: string;
  fmId: number;
  fmTitleId: string | null;
  title: string;
  view?: string;
  subHeadline: string;
  dataType: string;
}

export const Container: React.FC<ContainerProps> = (props) => {
  const view = React.useContext(viewContext);

  switch (view) {
    case "horizontal-data-display":
      return <RowViewContainer {...props} />;
    case "horizontal-card-carousel":
      return <CarouselViewContainer {...props} />;
    case "hotwire":
      return <HotwireContainer {...props} />;
    default:
      return <DefaultContainer {...props} />;
  }
};

const DefaultContainer: React.FC<Omit<ContainerProps, "view">> = ({
  children,
  fmId,
  fmTitleId,
  id,
  title,
  subHeadline,
}) => (
  <div className="CheckPrices all-y-padding-three" id={id} data-fm={fmId} data-fm-title-id={fmTitleId}>
    <UitkCard className="all-b-padding-three">
      <UitkCardContent>
        <UitkCardTextSegment className="all-y-padding-four">
          <UitkHeading level={2} size={600}>
            {title}
          </UitkHeading>
          {subHeadline && <span className="uitk-type-300 uitk-type-regular all-t-margin-one">{subHeadline}</span>}
        </UitkCardTextSegment>
        {children}
      </UitkCardContent>
    </UitkCard>
  </div>
);

const RowViewContainer: React.FC<Omit<ContainerProps, "view">> = ({
  children,
  fmId,
  fmTitleId,
  id,
  title,
  subHeadline,
}) => (
  <UitkSpacing padding={{ block: "three", inline: "four" }} margin="three">
    <section
      className="CheckPrices uitk-card uitk-cell uitk-flex-column"
      id={id}
      data-fm={fmId}
      data-fm-title-id={fmTitleId}
    >
      {title && (
        <UitkSpacing padding={{ blockend: "three" }}>
          <h2 className="uitk-type-heading-500">{title}</h2>
        </UitkSpacing>
      )}
      {subHeadline && (
        <UitkSpacing margin={{ blockstart: "one" }}>
          <span className="uitk-type-300 uitk-type-regular">{subHeadline}</span>
        </UitkSpacing>
      )}
      {title ? (
        <UitkSpacing padding={{ blockstart: "two" }}>
          <UitkLayoutFlex space="six" wrap="wrap">
            {children}
          </UitkLayoutFlex>
        </UitkSpacing>
      ) : (
        <UitkLayoutFlex space="six" wrap="wrap">
          {children}
        </UitkLayoutFlex>
      )}
    </section>
  </UitkSpacing>
);

const CarouselViewContainer: React.FC<Omit<ContainerProps, "view">> = ({
  children,
  fmId,
  fmTitleId,
  id,
  title,
  subHeadline,
}) => {
  const { formatText } = useLocalization();

  return (
    <section
      className="CheckPrices all-y-padding-three all-x-padding-three"
      id={id}
      data-fm={fmId}
      data-fm-title-id={fmTitleId}
    >
      <h2 className="uitk-type-heading-500">{title}</h2>
      {subHeadline && <span className="uitk-type-300 uitk-type-regular all-t-margin-one">{subHeadline}</span>}
      <IsomorphicCarousel
        className="CarouselView all-t-margin-three"
        carouselName="DestinationCards"
        buttonText={{
          nextButton: formatText("carousel.item.next"),
          prevButton: formatText("carousel.item.prev"),
        }}
        itemsVisible={{ sm: 2, md: 3, lg: 3 }}
        pageBy={1}
        peekWidth={12}
      >
        {children as React.ReactChild[]}
      </IsomorphicCarousel>
    </section>
  );
};
